import React, { useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Search, ShieldCheck, RotateCcw, Loader2, Clock, Smartphone } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  if (!iso) return 'No recorded activity';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

// Global-Administrator-only recovery tool: find a user and reset their stuck/
// stale session so they can sign in again. Reset ALL = revoke every session
// (force logout) + clear activity. Account, password, roles, TEAM shift and
// history are never touched.
const SessionManagement = () => {
  const { token } = useContext(AuthContext);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(null); // {user, scope, deviceId}
  const [busy, setBusy] = useState(false);

  const load = useCallback((q) => {
    setLoading(true);
    axios.get(`${API}/admin/sessions/lookup`, { ...auth, params: { search: q || '' } })
      .then((r) => setUsers(r.data.users || []))
      .catch((e) => toast.error(e?.response?.data?.detail || 'Lookup failed'))
      .finally(() => setLoading(false));
  }, [token]); // eslint-disable-line

  useEffect(() => { load(''); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => load(search), 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const doReset = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      const body = { user_id: confirm.user.id, scope: confirm.scope === 'device' ? confirm.deviceId : 'all' };
      const r = await axios.post(`${API}/admin/sessions/reset`, body, auth);
      toast.success(r.data.message || 'Session reset complete');
      setConfirm(null);
      load(search);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="session-management">
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 text-[#0E2A47]">
          <ShieldCheck size={20} />
          <h2 className="text-lg font-bold">Session Management</h2>
        </div>
        <p className="mt-1 text-sm text-slate-500">
          Reset a user's stuck, expired or corrupted session so they can sign in again.
          Resetting sessions never changes their account, password, roles, TEAM shift or history.
        </p>
        <div className="relative mt-4 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="session-search"
            className="min-h-[44px] w-full rounded-lg border border-slate-200 pl-9 pr-3 text-sm"
            placeholder="Search by name, email or personnel #"
            value={search} onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 p-6 text-slate-400"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      ) : users.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-400" data-testid="session-empty">No users match.</div>
      ) : (
        <div className="space-y-2" data-testid="session-user-list">
          {users.map((u) => (
            <div key={u.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`session-user-${u.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-[#0E2A47]">{u.name || u.email}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-600">{u.role_label}</span>
                    <span className={u.status === 'Inactive' ? 'text-amber-600' : ''}>{u.status}</span>
                    <span className="inline-flex items-center gap-1"><Smartphone size={12} /> {u.active_devices} active {u.active_devices === 1 ? 'device' : 'devices'}</span>
                    <span className="inline-flex items-center gap-1"><Clock size={12} /> {fmt(u.last_activity)}</span>
                  </div>
                </div>
                <button
                  data-testid={`session-reset-all-${u.id}`}
                  onClick={() => setConfirm({ user: u, scope: 'all' })}
                  className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-[#0E2A47] px-3 py-2 text-sm font-semibold text-white hover:bg-[#16324F]">
                  <RotateCcw size={15} /> Reset Sessions
                </button>
              </div>
              {u.devices && u.devices.length > 0 && (
                <div className="mt-3 space-y-1 border-t border-slate-100 pt-3">
                  {u.devices.map((d) => (
                    <div key={d.device_id} className="flex items-center justify-between gap-2 text-xs text-slate-500">
                      <span className="truncate font-mono">{d.device_id?.slice(0, 12)}… · {fmt(d.last_activity)}</span>
                      <button
                        data-testid={`session-reset-device-${u.id}`}
                        onClick={() => setConfirm({ user: u, scope: 'device', deviceId: d.device_id })}
                        className="shrink-0 rounded-md border border-slate-200 px-2 py-1 font-semibold text-[#0E2A47] hover:bg-slate-50">
                        Clear this device
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="session-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.scope === 'all' ? 'Reset all sessions?' : 'Clear this device?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.scope === 'all' ? (
                <>This will sign <span className="font-semibold">{confirm?.user?.name || confirm?.user?.email}</span> out of every device and clear their session state. They can sign in again immediately. Their account, password, roles, TEAM shift and history are not changed.</>
              ) : (
                <>This clears the stuck session state for one device of <span className="font-semibold">{confirm?.user?.name}</span>. Other devices are unaffected.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="session-confirm-cancel" disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="session-confirm-ok" onClick={(e) => { e.preventDefault(); doReset(); }} disabled={busy}>
              {busy ? <Loader2 size={15} className="animate-spin" /> : (confirm?.scope === 'all' ? 'Reset All Sessions' : 'Clear Device')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SessionManagement;
